import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import PropTypes from 'prop-types';

import { locationsIDs, Theme } from '@constants';
import { useContextReference, useNodeHandle } from '@hooks';

import { UserLocationsList } from './UserLocationsList';

import { isAndroid } from '../../../helpers/Platform';
import { FOCUS_NOW } from '../../../helpers/Tools';
import { TWCButton } from '../../focusable/TWCButton/TWCButton';
import { TWCText } from '../../primitives/TWCText/TWCText';

const tPath = 'locations.listPanel';

export const LocationsListPanel = ({ setVisiblePanel, data, deleteLocation, setDefault, setWatching }) => {
  const { t } = useTranslation('translation', { keyPrefix: tPath });

  const addButtonReference = useRef(null);
  const addButtonHandle = useNodeHandle(addButtonReference);
  const userLocationsListReference = useContextReference(locationsIDs.userLocationsList);

  const locationsListHandle = useNodeHandle(userLocationsListReference);

  useEffect(() => {
    if (data.length === 0) {
      setTimeout(() => {
        FOCUS_NOW(addButtonHandle);
      }, 10);
    }
  });

  return (
    <View>
      <TWCText style={styles.titleText} testID={locationsIDs.headingText} variant={'regularText60'}>
        {t('manageLocationsHeadingText')}
      </TWCText>
      <TWCButton
        accessibilityHint={t('addLocationButtonAccessibilityHint')}
        accessibilityLabel={t('addLocationButtonAccessibilityLabel')}
        nextFocusDown={locationsListHandle}
        nextFocusLeft={null}
        nextFocusRight={null}
        nextFocusUp={null}
        ref={addButtonReference}
        shouldUseContextReference={false}
        style={styles.addButton}
        testID={''}
        title={t('manageLocationAddLocationButtonText')}
        onPress={() => {
          setVisiblePanel({ key: 'add', locationId: null });
        }}
      />
      <View style={styles.listWrapper}>
        <TWCText
          importantForAccessibility={'no'}
          style={styles.subTitleText}
          testID={locationsIDs.citiesListHeadingText}
        >
          {t('manageLocationsListHeading')}
        </TWCText>

        <UserLocationsList
          addButtonReference={addButtonReference}
          data={data}
          deleteLocation={index => {
            deleteLocation(index);
            setTimeout(() => {
              FOCUS_NOW(userLocationsListReference);
            }, 10);
          }}
          setDefault={index => {
            setDefault(index);
            setTimeout(() => {
              FOCUS_NOW(userLocationsListReference);
            }, 10);
          }}
          setVisiblePanel={setVisiblePanel}
          setWatching={setWatching}
          style={styles.list}
        />
      </View>
    </View>
  );
};

LocationsListPanel.propTypes = {
  setVisiblePanel: PropTypes.func.isRequired,
  data: PropTypes.array.isRequired,
  deleteLocation: PropTypes.func.isRequired,
  setDefault: PropTypes.func.isRequired,
  setWatching: PropTypes.func.isRequired,
};

const styles = StyleSheet.create({
  addButton: {
    marginLeft: Theme.px(-26),
    marginTop: Theme.px(45),
  },
  errorText: {
    color: Theme.white,
    fontSize: Theme.px(30),
    height: '100%',
    justifyContent: 'center',
    marginLeft: Theme.px(-76),
    marginTop: Theme.px(100),
    textAlign: 'center',
  },
  hidden: {
    opacity: 0,
  },
  list: {
    marginLeft: Theme.px(-26),
  },
  listWrapper: {
    height: isAndroid ? Theme.px(774) : Theme.px(764),
    width: '100%',
  },
  loadingIndicator: {
    height: '100%',
    marginLeft: Theme.px(-76),
  },
  searchTextInput: {
    marginLeft: Theme.px(-26),
  },
  subTitleText: {
    color: Theme.whisperWhite,
    fontSize: Theme.px(30),
    marginBottom: Theme.px(11),
    marginTop: Theme.px(80),
  },
  titleText: {
    marginTop: Theme.px(65),
  },
});
